import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, X } from "lucide-react";

/**
 * Session 43 · Kompakter Sticky CTA
 * - Desktop: kleine Pille rechts unten, erscheint erst nach dem Hero
 * - Mobil: kompakte Sticky-Leiste am unteren Rand mit Schließen-Button
 * - Safe-Area (iOS) berücksichtigt
 * - Verdeckt keine Texte / FAQ / Chat
 */
export default function StickyCTA() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // erst nach dem Hero (~600 px) einblenden
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  useEffect(() => { setDismissed(false); }, [pathname]);

  if (
    pathname === "/" ||
    pathname.startsWith("/buchen") ||
    pathname.startsWith("/buchung") ||
    pathname.startsWith("/admin")
  ) {
    return null;
  }

  if (dismissed) return null;

  const isBusinessContext = pathname.startsWith("/businesskunden");
  const ctaTo = isBusinessContext ? "/buchen?type=business" : "/buchen";
  const ctaLabel = isBusinessContext ? "Anfrage senden" : t("sticky_cta.button");

  return (
    <>
      {/* Desktop: kompakte Pille */}
      <div
        className={`hidden md:block fixed z-40 right-6 bottom-6 transition-all duration-500 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        data-testid="sticky-cta"
      >
        <Link
          to={ctaTo}
          className="group inline-flex items-center gap-2 px-4 py-2.5 bg-ink-900/95 backdrop-blur-md border border-gold/60 hover:border-gold text-cream hover:bg-gold hover:text-ink-950 rounded-full shadow-lg transition-all duration-300"
        >
          <Calendar className="h-3.5 w-3.5 text-gold group-hover:text-ink-950 transition-colors" strokeWidth={1.5} />
          <span className="text-[0.7rem] uppercase tracking-[0.22em] font-medium whitespace-nowrap">
            {ctaLabel}
          </span>
        </Link>
      </div>

      {/* Mobil: kompakte Leiste unten mit Schließen */}
      <div
        className={`md:hidden fixed inset-x-0 z-40 bottom-0 transition-all duration-400 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="sticky-cta-mobile"
      >
        <div className="mx-3 mb-3 flex items-center gap-2 bg-ink-900/95 backdrop-blur-md border border-gold/60 rounded-full pl-4 pr-2 py-2 shadow-lg">
          <Link
            to={ctaTo}
            className="flex-1 inline-flex items-center gap-2 text-cream text-[0.72rem] uppercase tracking-[0.2em] font-medium"
          >
            <Calendar className="h-3.5 w-3.5 text-gold shrink-0" strokeWidth={1.5} />
            <span className="truncate">{ctaLabel}</span>
          </Link>
          <button
            type="button"
            aria-label="Schließen"
            onClick={() => setDismissed(true)}
            className="h-8 w-8 shrink-0 flex items-center justify-center text-cream/50 hover:text-cream"
            data-testid="sticky-cta-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}
